import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import {
  ArrowLeft, Play, CalendarDays, Plus, Search, Trash2, Upload, Settings2,
  Package, Bell, Mail, Check, X, Clock, LineChart,
} from "lucide-react";
import { api, apiError, money } from "../api";
import { PageLoader, EmptyState, StatusBadge, Spinner, Toast, useToast } from "../components/ui";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function toISODate() {
  const d = new Date();
  return d.toISOString().slice(0, 10);
}
function isoToDDMMYYYY(iso) {
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ProfileDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast, show, close } = useToast();

  const [profile, setProfile] = useState(null);
  const [products, setProducts] = useState([]);
  const [scans, setScans] = useState([]);
  const [search, setSearch] = useState("");
  const [showAllProducts, setShowAllProducts] = useState(false);
  const [busy, setBusy] = useState(false);

  const [showAdd, setShowAdd] = useState(false);
  const [pName, setPName] = useState("");
  const [pEan, setPEan] = useState("");

  const [showDate, setShowDate] = useState(false);
  const [customDate, setCustomDate] = useState(toISODate());

  const [showSettings, setShowSettings] = useState(false);

  const [historyProduct, setHistoryProduct] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openHistory = async (product) => {
    setHistoryProduct(product);
    setHistoryData(null);
    setHistoryLoading(true);
    try {
      const { data } = await api.get(`/profiles/${id}/products/${product.id}/history`);
      setHistoryData(data);
    } catch (e) {
      show(apiError(e), "error");
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadProfile = useCallback(async () => {
    const { data } = await api.get(`/profiles/${id}`);
    setProfile(data.profile);
  }, [id]);

  const loadProducts = useCallback(async (q = "") => {
    const { data } = await api.get(`/profiles/${id}/products`, { params: { search: q } });
    setProducts(data.products);
  }, [id]);

  const loadScans = useCallback(async () => {
    const { data } = await api.get("/scans", { params: { profile_id: id } });
    setScans(data.scans.slice(0, 5));
  }, [id]);

  useEffect(() => {
    loadProfile().catch((e) => show(apiError(e), "error"));
    loadProducts();
    loadScans();
  }, [loadProfile, loadProducts, loadScans]);

  const runScan = async (targetDate) => {
    setBusy(true);
    try {
      const body = targetDate ? { trigger: "custom", target_date: targetDate } : { trigger: "manual" };
      const { data } = await api.post(`/profiles/${id}/scan`, body);
      show("Scan started.");
      navigate(`/scans/${data.scan.id}`);
    } catch (e) {
      show(apiError(e), "error");
    } finally {
      setBusy(false);
      setShowDate(false);
    }
  };

  const addProduct = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/profiles/${id}/products`, { name: pName, ean: pEan });
      setPName(""); setPEan(""); setShowAdd(false);
      show("Product added.");
      loadProducts(search); loadProfile();
    } catch (err) {
      show(apiError(err), "error");
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post(`/profiles/${id}/products/import`, form);
      show(`${data.added} added${data.skipped_count ? `, ${data.skipped_count} skipped` : ""}.`);
      loadProducts(search); loadProfile();
    } catch (err) {
      show(apiError(err), "error");
    }
    e.target.value = "";
  };

  const removeProduct = async (pid) => {
    try {
      await api.delete(`/products/${pid}`);
      loadProducts(search); loadProfile();
    } catch (e) {
      show(apiError(e), "error");
    }
  };

  const deleteProfile = async () => {
    if (!window.confirm("Delete this profile and all its data?")) return;
    try {
      await api.delete(`/profiles/${id}`);
      navigate("/dashboard");
    } catch (e) {
      show(apiError(e), "error");
    }
  };

  const doSearch = (q) => { setSearch(q); loadProducts(q); };

  if (!profile) return <PageLoader label="Loading profile" />;

  const scheduleText =
    profile.weekdays?.length
      ? `${profile.weekdays.length === 7 ? "Daily" : (profile.weekdays || []).map((d) => WEEKDAYS[d]).join(", ")} · ${String(profile.scan_hour).padStart(2, "0")}:${String(profile.scan_minute).padStart(2, "0")}`
      : "No schedule";

  return (
    <div className="fade-in pb-6">
      <Toast toast={toast} onClose={close} />
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 text-sm text-muted mb-4" data-testid="detail-back">
        <ArrowLeft className="w-4 h-4" /> Dashboard
      </button>

      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h1 className="font-display text-2xl md:text-3xl text-ink truncate">{profile.name}</h1>
          <div className="flex items-center gap-2 mt-2">
            <span className="chip bg-teal-light text-teal">{profile.catalogue_label}</span>
            <span className="text-sm text-muted">{profile.product_count} products</span>
          </div>
        </div>
        <button className="btn-ghost" onClick={() => setShowSettings(true)} data-testid="detail-settings">
          <Settings2 className="w-4 h-4" />
        </button>
      </div>

      <div className="card p-4 mb-4">
        <div className="text-sm text-muted mb-3">Schedule: <span className="text-ink">{scheduleText}</span> <span className="text-muted">· Mauritius time</span></div>
        <div className="flex gap-2">
          <button className="btn-primary flex-1" onClick={() => runScan(null)} disabled={busy} data-testid="detail-run-now">
            {busy ? <Spinner /> : <Play className="w-4 h-4" />} Run now
          </button>
          <button className="btn-ghost flex-1" onClick={() => setShowDate(true)} data-testid="detail-custom-scan">
            <CalendarDays className="w-4 h-4" /> Custom date
          </button>
        </div>
      </div>

      {/* Recent scans */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-ink">Recent scans</h2>
        <button className="text-sm text-teal" onClick={() => navigate("/history")} data-testid="detail-view-history">View all</button>
      </div>
      {scans.length === 0 ? (
        <p className="text-sm text-muted mb-6">No scans yet.</p>
      ) : (
        <div className="card divide-y divide-stone-border mb-6">
          {scans.map((s) => (
            <button key={s.id} className="w-full flex items-center gap-3 p-3 text-left" onClick={() => navigate(`/scans/${s.id}`)} data-testid={`scan-row-${s.id}`}>
              <span className="flex-1">
                <span className="block text-ink">{s.target_date} · {s.trigger}</span>
                <span className="block text-xs text-muted">{s.changes_count} changes · {s.matched_count} matched</span>
              </span>
              <StatusBadge status={s.status} />
            </button>
          ))}
        </div>
      )}

      {/* Products */}
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-display text-lg text-ink">Products</h2>
        <div className="flex gap-2">
          <label className="btn-ghost cursor-pointer !px-3" data-testid="detail-import-label" title="Import CSV">
            <Upload className="w-4 h-4" />
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={importCsv} data-testid="detail-import-input" />
          </label>
          <button className="btn-accent !px-3" onClick={() => setShowAdd(true)} data-testid="detail-add-product">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
      </div>

      <div className="relative mb-3">
        <Search className="w-4 h-4 text-muted absolute left-3 top-1/2 -translate-y-1/2" />
        <input className="input pl-9" placeholder="Search by name or EAN (use * as wildcard)" value={search}
          onChange={(e) => doSearch(e.target.value)} data-testid="detail-search" />
      </div>

      {products.length === 0 ? (
        <EmptyState icon={Package} title="No products" description={search ? "No products match your search." : "Add products manually or import a CSV to start tracking prices."} testId="detail-products-empty" />
      ) : (
        <>
          <div className="card divide-y divide-stone-border" data-testid="detail-products-list">
            {(search || showAllProducts ? products : products.slice(0, 5)).map((p) => (
              <div key={p.id} className="flex items-center gap-3 p-3" data-testid={`product-${p.ean}`}>
                <span className="flex-1 min-w-0">
                  <span className="block text-ink truncate">{p.name}</span>
                  <span className="block text-xs text-muted font-mono">{p.ean}</span>
                </span>
                <button onClick={() => openHistory(p)} className="text-muted hover:text-teal min-h-[44px] px-2" aria-label="Price history" data-testid={`history-product-${p.ean}`}>
                  <LineChart className="w-4 h-4" />
                </button>
                <button onClick={() => removeProduct(p.id)} className="text-muted hover:text-rose min-h-[44px] px-2" aria-label="Delete product" data-testid={`delete-product-${p.ean}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
          {!search && products.length > 5 && (
            <button className="btn-ghost w-full mt-3" onClick={() => setShowAllProducts((v) => !v)} data-testid="detail-toggle-products">
              {showAllProducts ? "Show less" : `View all ${products.length} products`}
            </button>
          )}
        </>
      )}

      <button onClick={deleteProfile} className="btn-danger w-full mt-8" data-testid="detail-delete-profile">
        <Trash2 className="w-4 h-4" /> Delete profile
      </button>

      {showAdd && <AddModal {...{ pName, setPName, pEan, setPEan, addProduct, onClose: () => setShowAdd(false) }} />}
      {showDate && <DateModal {...{ customDate, setCustomDate, onRun: () => runScan(isoToDDMMYYYY(customDate)), onClose: () => setShowDate(false), busy }} />}
      {showSettings && <SettingsModal profile={profile} onClose={() => setShowSettings(false)} onSaved={() => { setShowSettings(false); loadProfile(); }} show={show} />}
      {historyProduct && <HistoryModal product={historyProduct} data={historyData} loading={historyLoading} onClose={() => setHistoryProduct(null)} />}
    </div>
  );
}

function HistoryModal({ product, data, loading, onClose }) {
  return (
    <Modal title="Price history" onClose={onClose}>
      <div data-testid="history-modal">
        <div className="mb-4">
          <div className="text-ink font-medium">{product.name}</div>
          <div className="text-xs text-muted font-mono">{product.ean}</div>
        </div>
        {loading ? (
          <div className="py-6 flex justify-center"><Spinner /></div>
        ) : !data || data.history.length === 0 ? (
          <p className="text-sm text-muted py-4" data-testid="history-empty">
            No recorded price changes yet. History builds up automatically as master scans detect price changes.
          </p>
        ) : (
          <>
            {data.current && (
              <div className="rounded-card bg-teal-light text-teal p-3 mb-4 text-sm" data-testid="history-current">
                Latest price <span className="font-bold">{money(data.current.mauritius_price)}</span>
                {data.current.effective_date && <span className="text-xs opacity-80"> · {data.current.effective_date}</span>}
              </div>
            )}
            <div className="divide-y divide-stone-border" data-testid="history-list">
              {data.history.map((h, i) => {
                const up = h.prev_price != null && h.mauritius_price > h.prev_price;
                const down = h.prev_price != null && h.mauritius_price < h.prev_price;
                return (
                  <div key={i} className="flex items-center justify-between py-2.5" data-testid={`history-point-${i}`}>
                    <div>
                      <div className="text-sm text-ink font-medium">{money(h.mauritius_price)}</div>
                      <div className="text-xs text-muted">{h.effective_date}</div>
                    </div>
                    <div className="text-xs text-right">
                      {h.prev_price == null ? (
                        <span className="text-muted">first recorded</span>
                      ) : (
                        <span className={up ? "text-rose font-medium" : down ? "text-emerald font-medium" : "text-muted"}>
                          {up ? "▲" : down ? "▼" : ""} from {money(h.prev_price)}
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);
  return createPortal(
    <div className="fixed inset-0 z-50 bg-ink/40 flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose} data-testid="modal-overlay">
      <div className="card w-full max-w-md p-5 fade-in max-h-[90vh] overflow-y-auto rounded-b-none md:rounded-card" onClick={(e) => e.stopPropagation()} data-testid="modal-content">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-lg text-ink">{title}</h3>
          <button onClick={onClose} className="text-muted" aria-label="Close" data-testid="modal-close"><X className="w-5 h-5" /></button>
        </div>
        {children}
      </div>
    </div>,
    document.body
  );
}

function AddModal({ pName, setPName, pEan, setPEan, addProduct, onClose }) {
  return (
    <Modal title="Add product" onClose={onClose}>
      <form onSubmit={addProduct} className="space-y-4" data-testid="add-product-form">
        <div>
          <label className="label" htmlFor="np">Product name</label>
          <input id="np" className="input" required value={pName} onChange={(e) => setPName(e.target.value)} data-testid="add-name" />
        </div>
        <div>
          <label className="label" htmlFor="ne">EAN code</label>
          <input id="ne" className="input font-mono" required value={pEan} placeholder="8, 12, 13 or 14 digits"
            onChange={(e) => setPEan(e.target.value)} data-testid="add-ean" />
        </div>
        <button type="submit" className="btn-primary w-full" data-testid="add-submit"><Check className="w-4 h-4" /> Add product</button>
      </form>
    </Modal>
  );
}

function DateModal({ customDate, setCustomDate, onRun, onClose, busy }) {
  return (
    <Modal title="Scan a specific date" onClose={onClose}>
      <label className="label" htmlFor="cd">Target date</label>
      <input id="cd" type="date" className="input mb-4" value={customDate} onChange={(e) => setCustomDate(e.target.value)} data-testid="custom-date-input" />
      <button className="btn-primary w-full" onClick={onRun} disabled={busy} data-testid="custom-date-run">
        {busy ? <Spinner /> : <Play className="w-4 h-4" />} Run scan
      </button>
    </Modal>
  );
}

function SettingsModal({ profile, onClose, onSaved, show }) {
  const [hour, setHour] = useState(profile.scan_hour);
  const [minute, setMinute] = useState(profile.scan_minute);
  const [weekdays, setWeekdays] = useState(profile.weekdays || []);
  const [notifyPush, setNotifyPush] = useState(profile.notify_push);
  const [notifyEmail, setNotifyEmail] = useState(profile.notify_email);
  const [name, setName] = useState(profile.name);
  const [saving, setSaving] = useState(false);

  const toggleDay = (d) => setWeekdays((w) => ((w || []).includes(d) ? (w || []).filter((x) => x !== d) : [...(w || []), d].sort()));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/profiles/${profile.id}`, {
        name, scan_hour: hour, scan_minute: minute, weekdays,
        notify_push: notifyPush, notify_email: notifyEmail,
      });
      show("Profile updated.");
      onSaved();
    } catch (e) {
      show(apiError(e), "error");
      setSaving(false);
    }
  };

  return (
    <Modal title="Profile settings" onClose={onClose}>
      <div className="space-y-4" data-testid="settings-modal">
        <div>
          <label className="label" htmlFor="sn">Name</label>
          <input id="sn" className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="settings-name" />
        </div>
        <p className="text-xs text-muted flex items-center gap-1.5" data-testid="settings-tz-note">
          <Clock className="w-3.5 h-3.5" /> Scan times are in Mauritius time (Indian/Mauritius).
        </p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="sh">Hour</label>
            <select id="sh" className="input" value={hour} onChange={(e) => setHour(Number(e.target.value))} data-testid="settings-hour">
              {Array.from({ length: 24 }).map((_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}</option>)}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="sm">Minute</label>
            <select id="sm" className="input" value={minute} onChange={(e) => setMinute(Number(e.target.value))} data-testid="settings-minute">
              {[0, 15, 30, 45].map((m) => <option key={m} value={m}>{String(m).padStart(2, "0")}</option>)}
            </select>
          </div>
        </div>
        <div>
          <span className="label">Days</span>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((d, i) => (
              <button key={d} onClick={() => toggleDay(i)}
                className={`min-h-[44px] px-3 rounded-card border text-sm ${weekdays.includes(i) ? "bg-teal text-white border-teal" : "border-stone-border text-ink"}`}
                data-testid={`settings-day-${i}`}>{d}</button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setNotifyPush((v) => !v)} className={`btn ${notifyPush ? "bg-teal-light text-teal" : "btn-ghost"} flex-1`} data-testid="settings-push">
            <Bell className="w-4 h-4" /> Push {notifyPush ? "on" : "off"}
          </button>
          <button onClick={() => setNotifyEmail((v) => !v)} className={`btn ${notifyEmail ? "bg-teal-light text-teal" : "btn-ghost"} flex-1`} data-testid="settings-email">
            <Mail className="w-4 h-4" /> Email {notifyEmail ? "on" : "off"}
          </button>
        </div>
        <button className="btn-primary w-full" onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <Spinner /> : <Check className="w-4 h-4" />} Save changes
        </button>
      </div>
    </Modal>
  );
}
